import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Grid,
  MenuItem,
  Chip,
  Link,
  InputAdornment,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
// import { toast } from "../../common/AppToast";
import { filledFieldWrapSx, filledUploadDropzoneSx } from "./listNewAssetFilledSurface";

const MAX_FILE_MB = 10;

const DIVIDEND_FREQUENCIES = ["Monthly", "Quarterly", "Semi-Annually", "Annually", "Ad-hoc"];

const labelSx = { fontWeight: 600, fontSize: 14, color: "text.primary", mb: 0.75 };

function RequiredLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}{" "}
      <Box component="span" sx={{ color: "error.main" }}>
        *
      </Box>
    </Typography>
  );
}

function parseAmount(str) {
  const n = parseFloat(String(str).replace(/,/g, "").replace(/^SAR\s*/i, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function parsePercent(str) {
  const n = parseFloat(String(str).replace(/%/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

function sanitizeNumeric(v) {
  const s = v.replace(/[^\d.]/g, "");
  const i = s.indexOf(".");
  return i === -1 ? s : s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
}

function sanitizeInteger(v) {
  return v.replace(/\D/g, "");
}

export default function ListNewAssetStep5({
  draft,
  onDraftChange,
  nextStep,
  prevStep,
  wizardStep = 5,
  wizardTotalSteps = 8,
  categoryChipLabel = "Real Estate",
  isEditMode = false,
}) {
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;

  // Borderless flat-input style — matches Asset Name / Description in Step2.
  const flatInputSx = {
    "& .MuiInputBase-input": {
      px: 1,
      py: 1.25,
      fontSize: 14,
    },
  };

  const totalAssetValue = draft.totalAssetValue ?? "";
  const pricePerTokenSar =
    draft.pricePerTokenSar === undefined || draft.pricePerTokenSar === null
      ? "10"
      : draft.pricePerTokenSar;
  const expectedRoi = draft.expectedRoi ?? "";
  const rentalYield = draft.rentalYield ?? "";
  const dividendFrequency = draft.dividendFrequency ?? "";
  const valuationReport = draft.valuationReport;

  const fileInputRef = useRef(null);

  const assetValueNum = useMemo(() => parseAmount(totalAssetValue), [totalAssetValue]);
  const pricePerTokenNum = useMemo(() => parseAmount(pricePerTokenSar), [pricePerTokenSar]);

  const projectedSupply = useMemo(() => {
    if (assetValueNum <= 0 || pricePerTokenNum <= 0) return 0;
    return Math.max(0, Math.floor(assetValueNum / pricePerTokenNum));
  }, [assetValueNum, pricePerTokenNum]);

  const handleValuationFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setErrors((p) => ({ ...p, valuationReport: "Only PDF files are allowed." }));
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, valuationReport: `File must be ${MAX_FILE_MB}MB or smaller.` }));
      return;
    }
    onDraftChange({ valuationReport: file });
    setErrors((p) => ({ ...p, valuationReport: "" }));
  };

  const handleNext = () => {
    const roi = parsePercent(expectedRoi);
    const rental = parsePercent(rentalYield);
    const newErrors = {};
    if (assetValueNum <= 0) newErrors.totalAssetValue = "Please enter a valid total asset value.";
    if (pricePerTokenNum <= 0) newErrors.pricePerTokenSar = "Please enter a valid price per token (SAR).";
    if (!Number.isFinite(roi)) newErrors.expectedRoi = "Please enter expected ROI (%).";
    if (!Number.isFinite(rental)) newErrors.rentalYield = "Please enter expected rental yield (%).";
    if (!String(dividendFrequency || "").trim()) {
      newErrors.dividendFrequency = "Please select dividend frequency.";
    }
    const hasValuation =
      valuationReport ||
      (isEditMode && Boolean(draft._editMeta?.valuationReportUrl));
    if (!hasValuation) newErrors.valuationReport = "Please upload your valuation report (PDF).";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    nextStep();
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1100,
        mx: "auto",
        minHeight: "100vh",
      }}
    >

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={prevStep}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: TEAL,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: TEAL_HOVER },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
          Back
        </Link>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={categoryChipLabel}
            size="small"
            sx={{
              bgcolor: (theme) => theme.palette.brand.cyanSoft,
              color: (theme) => theme.palette.brand.cyanDark,
              fontWeight: 600,
              fontSize: 12,
              height: 26,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
            Step {wizardStep} of {wizardTotalSteps}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
        {Array.from({ length: wizardTotalSteps }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              bgcolor: i < wizardStep ? TEAL : theme.palette.border.light,
            }}
          />
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: "14px",
          bgcolor: "background.paper",
          boxShadow: (theme) => theme.palette.shadow.cardStrong,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}
        >
          Financial Details
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
          Set the valuation and investment parameters
        </Typography>

        {/* Independent Valuation */}
        {/* <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "12px",
            bgcolor: (theme) => theme.palette.brand.cyanSoft,
            border: (theme) => `1px solid ${theme.palette.accent.info.border}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { sm: "flex-start" },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1.5,
              bgcolor: (theme) => theme.palette.accent.info.border,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: (theme) => theme.palette.accent.info.icon,
              flexShrink: 0,
            }}
          >
            <ShieldOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: (theme) => theme.palette.accent.info.text,
                mb: 1,
              }}
            >
              Independent Valuation Required
            </Typography>
            <Typography
              sx={{ fontSize: 14, color: "text.neutral", lineHeight: 1.6, mb: 1.5 }}
            >
              All assets must be valued by an accredited 3rd party evaluator. For Saudi Arabian
              assets, we recommend{" "}
              <Box component="strong" sx={{ fontWeight: 700 }}>
                TAQEEM (Saudi Authority for Accredited Valuers)
              </Box>{" "}
              certified members.
            </Typography>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "success.main",
                color: "background.paper",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.5,
              }}
            >
              TAQEEM
            </Box>
          </Box>
        </Paper> */}

        {/* Valuation Report Analysis */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: (theme) => theme.palette.brand.cyanSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: (theme) => theme.palette.brand.cyan,
                flexShrink: 0,
              }}
            >
              <BarChartOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "text.primary" }}>
                Valuation Report Analysis{" "}
                <Box component="span" sx={{ color: "error.main" }}>*</Box>
              </Typography>
              <Typography sx={{ fontSize: 14, color: "text.muted", mt: 0.5 }}>
                Upload your official valuation report to automatically extract asset value and ROI
                projections.
              </Typography>
            </Box>
          </Stack>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={handleValuationFile}
          />
          <Paper
            component="button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            elevation={0}
            sx={{
              width: "100%",
              minHeight: 140,
              mt: 2,
              borderRadius: 2,
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              p: 2,
              cursor: "pointer",
              "&:hover": {
                borderColor: (theme) => theme.palette.brand.cyan,
                bgcolor: "background.subtle",
              },
              ...filledUploadDropzoneSx(
                theme,
                Boolean(valuationReport || draft._editMeta?.valuationReportUrl),
                theme.palette.accent.teal.dashedBorder
              ),
            }}
          >
            <Stack alignItems="center" spacing={0.5}>
              <CloudUploadOutlinedIcon
                sx={{
                  color: (theme) => theme.palette.brand.cyan,
                  fontSize: 36,
                }}
              />
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: (theme) => theme.palette.brand.cyanDark,
                }}
              >
                Click to upload report
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.muted" }}>
                PDF up to 10MB
              </Typography>
              {(valuationReport || draft._editMeta?.valuationReportUrl) && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: (theme) => theme.palette.brand.cyanDark,
                    fontWeight: 600,
                  }}
                >
                  {valuationReport?.name || "Current valuation report on file"}
                </Typography>
              )}
            </Stack>
          </Paper>
          {errors.valuationReport && (
            <FormHelperText error sx={{ mt: 0.5 }}>{errors.valuationReport}</FormHelperText>
          )}
        </Box>

        {/* Financial inputs */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={filledFieldWrapSx(theme, assetValueNum > 0)}>
              <RequiredLabel>Total Asset Value (SAR)</RequiredLabel>
              <TextField
                variant="standard"
                fullWidth
                placeholder="0"
                value={totalAssetValue}
                onChange={(e) => { onDraftChange({ totalAssetValue: sanitizeInteger(e.target.value) }); setErrors((p) => ({ ...p, totalAssetValue: "" })); }}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <Typography sx={{ color: "text.muted", fontSize: 14 }}>SAR</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: "numeric" }}
                sx={{ mt: 1, ...flatInputSx }}
              />
            </Box>
            {errors.totalAssetValue && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.totalAssetValue}</FormHelperText>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={filledFieldWrapSx(theme, Number.isFinite(parsePercent(expectedRoi)))}>
              <RequiredLabel>Expected ROI (%)</RequiredLabel>
              <TextField
                variant="standard"
                fullWidth
                placeholder="0.0"
                value={expectedRoi}
                onChange={(e) => { onDraftChange({ expectedRoi: sanitizeNumeric(e.target.value) }); setErrors((p) => ({ ...p, expectedRoi: "" })); }}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <Typography sx={{ color: "text.muted", fontSize: 14 }}>%</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1, ...flatInputSx }}
              />
            </Box>
            {errors.expectedRoi && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.expectedRoi}</FormHelperText>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={filledFieldWrapSx(theme, Number.isFinite(parsePercent(rentalYield)))}>
              <RequiredLabel>Expected Rental Yield (%)</RequiredLabel>
              <TextField
                variant="standard"
                fullWidth
                placeholder="0.0"
                value={rentalYield}
                onChange={(e) => {
                  onDraftChange({ rentalYield: sanitizeNumeric(e.target.value) });
                  setErrors((p) => ({ ...p, rentalYield: "" }));
                }}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <Typography sx={{ color: "text.muted", fontSize: 14 }}>%</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1, ...flatInputSx }}
              />
            </Box>
            {errors.rentalYield && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.rentalYield}</FormHelperText>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={filledFieldWrapSx(theme, !!dividendFrequency)}>
              <RequiredLabel>Dividend Frequency</RequiredLabel>
              <TextField
                variant="standard"
                select
                fullWidth
                value={dividendFrequency}
                onChange={(e) => {
                  onDraftChange({ dividendFrequency: e.target.value });
                  setErrors((p) => ({ ...p, dividendFrequency: "" }));
                }}
                InputProps={{ disableUnderline: true }}
                sx={{ mt: 1, ...flatInputSx }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? (
                      v
                    ) : (
                      <Typography sx={{ color: "text.faint" }}>Select frequency</Typography>
                    ),
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select frequency</em>
                </MenuItem>
                {DIVIDEND_FREQUENCIES.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {errors.dividendFrequency && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.dividendFrequency}</FormHelperText>
            )}
          </Grid>
        </Grid>

        {/* Token Economics (Projected) */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: "12px",
            border: (theme) => `1px solid ${theme.palette.border.light}`,
            bgcolor: "background.subtle",
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: 16, color: "text.primary", mb: 2 }}
          >
            Token Economics (Projected)
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "12px",
                  border: (theme) => `1px solid ${theme.palette.border.light}`,
                  bgcolor: "background.paper",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 100,
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: "text.muted" }}
                >
                  Total Token Supply
                </Typography>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "text.primary",
                    mt: 1,
                  }}
                >
                  {new Intl.NumberFormat("en-SA").format(projectedSupply)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "12px",
                  border: (theme) => `1px solid ${theme.palette.border.light}`,
                  bgcolor: "background.paper",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  minHeight: 100,
                }}
              >
                <Box sx={filledFieldWrapSx(theme, pricePerTokenNum > 0)}>
                  <RequiredLabel>Price Per Token (SAR)</RequiredLabel>
                  <TextField
                    fullWidth
                    placeholder="0.00"
                    value={pricePerTokenSar}
                    onChange={(e) => { onDraftChange({ pricePerTokenSar: sanitizeNumeric(e.target.value) }); setErrors((p) => ({ ...p, pricePerTokenSar: "" })); }}
                    sx={{ mt: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: "#6b7280", fontSize: 14 }}>SAR</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {errors.pricePerTokenSar && (
                  <FormHelperText error sx={{ mt: 0.5 }}>{errors.pricePerTokenSar}</FormHelperText>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 2 }}
        >
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: (theme) => theme.palette.border.medium,
              color: "text.neutral",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: (theme) => theme.palette.text.faint,
                bgcolor: "background.subtle",
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={handleNext}
            sx={{
              px: 1.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: "12px",
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
